import { useId, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Checkbox, CircularProgress, Pagination } from "@mui/material";
import { inlineChoiceControlSx } from "../../common/dashboard/formStyles";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  ChamberMemberProGovernanceSettingsReturn,
  CmpEventStatusEnum,
  CmpMemberLifecycleStatusEnum,
  CmpNewsletterRecipientSourceKindEnum,
  GetCmpManageEventsQuery,
  GetCmpNewsletterRecipientGroupsDocument,
  GetCmpNewsletterRecipientGroupsQuery,
  UpdateCmpNewsletterRecipientGroupDocument,
  PreviewCmpNewsletterRecipientsDocument,
  useCreateCmpNewsletterRecipientGroupMutation,
  useGetAllCategoriesQuery,
  useGetCmpManageEventsQuery,
  useGetCmpManageMembersQuery,
} from "../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import {
  useCmpGovernanceSettings,
  useCmpMembershipSettings,
} from "../../../hooks/useCmpSettingsSection";
import { ModalShell } from "../../common/dashboard/ModalShell";
import { ConfirmDialog } from "../../common/dashboard/ConfirmDialog";
import { ActionSuccessDialog } from "../../common/dashboard/ActionSuccessDialog";
import { isTierMembershipModel } from "../MyChamber/ChamberSettings/settingsMappers";
import { DataTable, DataTableColumn } from "../../common/dashboard/DataTable";
import {
  CUSTOM_AUDIENCE_RECIPIENT_REMOVAL_UNAVAILABLE_MESSAGE,
  removeRecipientFromCustomAudience,
} from "./customAudienceRecipientRemoval";
import {
  EMAIL_CHAMBER_LOADING_MESSAGE,
  emailLoadErrorText,
  EMAIL_OPERATION_ERROR_CONTEXT,
  getCustomListContactCount,
  previewQueryToSummary,
  recipientGroupReturnToUiGroup,
} from "./emailBackendMappers";
import { applyCmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../utils/userFacingError";
import { nextEmailManageSort, EmailManageSort } from "./emailManageActions";
import { SectionShell } from "../../common/dashboard/SectionShell";
import {
  EmailAdditionalRecipient,
  EmailAudienceMemberOption,
  EmailFormState,
  RecipientGroup,
  RecipientSourceId,
  RecipientPreviewSummary,
} from "./emailMockData";
import {
  AudienceFilterId,
  AudienceFilters,
  buildAudienceFilterSummary,
  buildRecipientSourceRows,
  EmailDesignScope,
  inputClassName,
  FilterDropdown,
  SectionHeading,
  UpdateEmailField,
  dangerButtonClassName,
  formatNumber,
  getAudienceRangeOptions,
  primaryButtonClassName,
  secondaryButtonClassName,
} from "./emailWizardShared";

export type StepOneAudienceProps = {
  email: EmailFormState;
  recipientGroups: RecipientGroup[];
  memberTypeOptions: Array<{ id: string; label: string }>;
  audienceFilters: AudienceFilters;
  intendedRecipientCount: number | null;
  isAudiencePreviewLoading: boolean;
  audiencePreviewError: string;
  isBusy: boolean;
  isSentRecord: boolean;
  toggleRecipientSource: (sourceId: RecipientSourceId) => void;
  toggleRecipientGroup: (groupId: string) => void;
  archiveRecipientGroup: (group: RecipientGroup) => Promise<void>;
  updateAudienceFilter: (filterId: AudienceFilterId, values: string[]) => void;
  clearAllFilters: () => void;
  updateEmailField: UpdateEmailField;
  recipientPreview?: RecipientPreviewSummary | null;
  onRefreshRecipients?: () => void;
};

// Continuously managed audiences are first-class backend sources. Custom lists
// retain saved criteria and legacy member snapshots. Claude Design (16)
// Create Email Step 1: nine plain audiences, then three audiences that each
// open their own picker.
export const ESTIMATED_AUDIENCE_MODAL_TITLE =
  "Estimated Audience (Deleting a recipient only removes them from this email campaign.)";

export const audienceCheckboxes: Array<{ id: RecipientSourceId; label: string; helper: string }> = [
  { id: "allMembers", label: "Full Email Subscribers Lists", helper: "Pulled from the member directory, all email subscribers from the website, and any manual entries." },
  { id: "activePrimaryContacts", label: "Primary Contact (Voting Member)", helper: "Pulled from the Manage Member page Primary Contact" },
  { id: "activeMembers", label: "Active Members", helper: "Pulled from the Active Member directory. Will include all Primary Contacts and any other contact emails associated with this member" },
  { id: "renewingMembers", label: "Renewing Members (within 60 days)", helper: "Pulled from members who are up for renewal in the next 60 days" },
  { id: "newMembers", label: "New Members (within 60 days)", helper: "Pulled from members who have joined in the last 60 days" },
  { id: "membersOfferingPerks", label: "Members Offering Perks", helper: "Pulled from members who are offering perks" },
  { id: "prospectMembers", label: "Prospects", helper: "Pulled from the Manage Prospects page. Includes all prospect contacts who have not yet joined" },
  { id: "pendingMembers", label: "Pending Members", helper: "Pulled from members whose application or first payment has not yet been approved or received" },
  { id: "suspendedMembers", label: "Suspended Members", helper: "Pulled from members whose membership is suspended, such as for non-payment of renewal" },
];

export const audiencePickerSources: Array<{ id: RecipientSourceId; label: string; helper: string; pickerLabel: string }> = [
  { id: "eventAttendees", label: "Event Attendees", helper: "Pulled from the confirmed attendees of the selected events, including non-members and sponsor attendees", pickerLabel: "Select Events" },
  { id: "governanceGroups", label: "Governance and Groups", helper: "Pulled from the members of the selected boards, committees, and groups", pickerLabel: "Select Groups" },
  { id: "eventSponsors", label: "Sponsors", helper: "Pulled from the sponsor contacts of the selected events, including sponsors who are non-members", pickerLabel: "Select Events" },
];

/** Board, committee and task force options for "Select Groups"; a saved group that was deleted stays visible so staff can remove it. */
export const buildGovernanceGroupOptions = ({
  governance,
  selectedGroupIds,
}: {
  governance?: Pick<ChamberMemberProGovernanceSettingsReturn, "boards" | "committees" | "taskForces"> | null;
  selectedGroupIds: ReadonlyArray<string>;
}): Array<{ id: string; label: string }> => {
  const options = [...(governance?.boards || []), ...(governance?.committees || []), ...(governance?.taskForces || [])]
    .map((group) => ({ id: group.id, label: group.name }));
  const known = new Set(options.map((option) => option.id));
  return [
    ...options,
    ...selectedGroupIds.filter((id) => !known.has(id)).map((id) => ({ id, label: "Group no longer available" })),
  ];
};

type ManageEventOption = Pick<
  GetCmpManageEventsQuery["getCmpManageEvents"]["events"][number],
  "id" | "status" | "startAtUtcMilli" | "timeZone" | "title"
>;

/**
 * Event picker options for the Event Attendees audience: every event that
 * can have attendees (drafts excluded), newest first, labelled with the date
 * in the event's own time zone. A saved event that is no longer listed stays
 * visible so staff can see and remove it.
 */
export const buildEventAttendeeEventOptions = ({
  events,
  selectedEventIds,
}: {
  events: ReadonlyArray<ManageEventOption>;
  selectedEventIds: ReadonlyArray<string>;
}): Array<{ id: string; label: string }> => {
  const listedEvents = events
    .filter((event) => event.status !== CmpEventStatusEnum.Draft)
    .sort((first, second) => second.startAtUtcMilli - first.startAtUtcMilli);
  const listedIds = new Set(listedEvents.map((event) => event.id));

  return [
    ...listedEvents.map((event) => ({
      id: event.id,
      label: `${event.title} (${new Date(event.startAtUtcMilli).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        ...(event.timeZone ? { timeZone: event.timeZone } : {}),
      })})`,
    })),
    ...selectedEventIds
      .filter((eventId) => !listedIds.has(eventId))
      .map((eventId) => ({ id: eventId, label: "Event no longer available" })),
  ];
};

type PopupCategoryOption = {
  id: string;
  label: string;
  disabledReason?: string;
};

// The custom audience keeps ordinary member snapshots separate from live
// governance and prospect sources. Its size options follow the popup design.
const popupMemberStatusOptions: PopupCategoryOption[] = [
  { id: "status:ACTIVE", label: "Active Members" },
  { id: "status:PRIMARY_ACTIVE", label: "Primary Contact (Voting Member)" },
  { id: "status:PROSPECT", label: "Prospect Members" },
  { id: "status:PENDING", label: "Pending Members" },
  { id: "status:SUSPENDED", label: "Suspended Members" },
  {
    id: "status:DELETED",
    label: "Deleted Members",
    disabledReason: "Deleted members cannot receive emails.",
  },
  { id: "status:new30", label: "New Members (within 30 days)" },
  { id: "status:new60", label: "New Members (within 60 days)" },
];
const popupMemberSinceOptions: PopupCategoryOption[] = [
  { id: "since:<1", label: "Less than 1 Year" },
  { id: "since:1-3", label: "1\u20133 Years" },
  { id: "since:3-5", label: "3\u20135 Years" },
  { id: "since:5-10", label: "5\u201310 Years" },
  { id: "since:10+", label: "10+ Years" },
];
const popupRenewalStatusOptions: PopupCategoryOption[] = [
  { id: "renewal:within30", label: "Renewing Within 30 Days" },
  { id: "renewal:within60", label: "Renewing Within 60 Days" },
  { id: "renewal:expired", label: "Expired Memberships" },
  { id: "renewal:recent", label: "Recently Renewed" },
];

const dayMilli = 24 * 60 * 60 * 1000;
const yearMilli = 365.25 * dayMilli;

const getEmployeeCount = (member: EmailAudienceMemberOption) =>
  (member.fullTimeEmployees || 0) + (member.partTimeEmployees || 0);

// A member belongs to a popup category when it matches that category's
// bucket. The popup audience is the UNION of every checked category (each
// checkbox adds that group of members), plus any typed-in email addresses.
type MemberMatchesPopupCategoryInput = {
  member: EmailAudienceMemberOption;
  categoryOptionId: string;
  nowUtcMilli?: number;
};

export const memberMatchesPopupCategory = ({
  member,
  categoryOptionId,
  nowUtcMilli = Date.now(),
}: MemberMatchesPopupCategoryInput): boolean => {
  const separatorIndex = categoryOptionId.indexOf(":");
  const group = categoryOptionId.slice(0, separatorIndex);
  const value = categoryOptionId.slice(separatorIndex + 1);

  if (group === "status") {
    if (value === "new30" || value === "new60") {
      const windowMilli = value === "new30" ? 30 * dayMilli : 60 * dayMilli;
      return (
        !!member.dateJoinedUtcMilli &&
        member.dateJoinedUtcMilli >= nowUtcMilli - windowMilli &&
        member.dateJoinedUtcMilli <= nowUtcMilli
      );
    }
    return member.lifecycleStatus === value;
  }
  if (group === "type") {
    return member.membershipTypeId === value;
  }
  if (group === "size") {
    const employeeCount = getEmployeeCount(member);
    if (value === "1-5") return employeeCount >= 1 && employeeCount <= 5;
    if (value === "6-20") return employeeCount >= 6 && employeeCount <= 20;
    if (value === "21-50") return employeeCount >= 21 && employeeCount <= 50;
    if (value === "51-100") return employeeCount >= 51 && employeeCount <= 100;
    if (value === "101-250")
      return employeeCount >= 101 && employeeCount <= 250;
    return value === "250+" && employeeCount > 250;
  }
  if (group === "industry") {
    return (member.categoryId || []).includes(value);
  }
  if (group === "since") {
    if (!member.dateJoinedUtcMilli || member.dateJoinedUtcMilli > nowUtcMilli)
      return false;
    const years = (nowUtcMilli - member.dateJoinedUtcMilli) / yearMilli;
    if (value === "<1") return years < 1;
    if (value === "1-3") return years >= 1 && years < 3;
    if (value === "3-5") return years >= 3 && years < 5;
    if (value === "5-10") return years >= 5 && years < 10;
    return value === "10+" && years >= 10;
  }
  if (group !== "renewal") return false;
  if (value === "recent") {
    return (
      !!member.lastRenewedAtUtcMilli &&
      member.lastRenewedAtUtcMilli >= nowUtcMilli - 60 * dayMilli &&
      member.lastRenewedAtUtcMilli <= nowUtcMilli
    );
  }
  if (!member.renewalDateUtcMilli) return false;
  const timeUntilRenewal = member.renewalDateUtcMilli - nowUtcMilli;
  if (value === "within30")
    return timeUntilRenewal >= 0 && timeUntilRenewal < 30 * dayMilli;
  if (value === "within60")
    return timeUntilRenewal >= 0 && timeUntilRenewal < 60 * dayMilli;
  return (
    value === "expired" &&
    member.lifecycleStatus === "ACTIVE" &&
    timeUntilRenewal < 0
  );
};

const isPlausibleEmail = (address: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

const popupInputClassName =
  "h-[43px] w-full rounded-lg border border-[#9E9E9E] bg-white px-3 text-[14px] text-[#0A0A0A] outline-none focus:border-cpaGold focus:ring-2 focus:ring-cpaGold/20 disabled:bg-gray-100";

export type CreateCustomAudiencePayload = {
  name: string;
  memberIds: string[];
  eventIds?: string[];
  description?: string | null;
  criteriaIds: string[];
  governanceGroupIds: string[];
  sourceKinds: CmpNewsletterRecipientSourceKindEnum[];
  directEmails: string[];
  additionalRecipients: EmailAdditionalRecipient[];
};

type BuildCustomAudiencePayloadInput = {
  initialGroup?: RecipientGroup;
  name: string;
  selectedCategoryIds: string[];
  memberOptions: EmailAudienceMemberOption[];
  additionalRecipients: EmailAdditionalRecipient[];
  nowUtcMilli?: number;
};

export const buildCustomAudiencePayload = ({
  name,
  selectedCategoryIds,
  additionalRecipients,
  initialGroup,
}: BuildCustomAudiencePayloadInput): CreateCustomAudiencePayload => {
  const governanceGroupIds = Array.from(
    new Set(
      selectedCategoryIds
        .filter((id) => id.startsWith("governance:"))
        .map((id) => id.slice("governance:".length))
    )
  );
  const previous = initialGroup?.selection;
  const previousNamedEmails = new Set((previous?.additionalRecipients || []).map(recipient => recipient.email.trim().toLowerCase()));
  const directEmails = Array.from(new Set([
    ...(previous?.directEmails || []).filter(address => !previousNamedEmails.has(address.trim().toLowerCase())),
    ...additionalRecipients.map(recipient => recipient.email.trim().toLowerCase()),
  ]));
  return {
    name: name.trim(),
    memberIds: [...(previous?.memberIds || [])],
    ...(previous ? { eventIds: [...(previous.eventIds || [])], description: previous.description } : {}),
    criteriaIds: Array.from(new Set(selectedCategoryIds.filter(id => !id.startsWith("governance:") && id !== "status:PROSPECT"))),
    governanceGroupIds,
    sourceKinds: [
      ...(previous?.sourceKinds || []).filter(kind => ![
        CmpNewsletterRecipientSourceKindEnum.GovernanceGroups,
        CmpNewsletterRecipientSourceKindEnum.DirectEmails,
        CmpNewsletterRecipientSourceKindEnum.ProspectMembers,
      ].includes(kind)),
      ...(governanceGroupIds.length > 0
        ? [CmpNewsletterRecipientSourceKindEnum.GovernanceGroups]
        : []),
      ...(directEmails.length > 0
        ? [CmpNewsletterRecipientSourceKindEnum.DirectEmails]
        : []),
      ...(selectedCategoryIds.includes("status:PROSPECT")
        ? [CmpNewsletterRecipientSourceKindEnum.ProspectMembers]
        : []),
    ],
    directEmails,
    additionalRecipients: toNamedRecipientInputs(additionalRecipients),
  };
};

// The design requires only the email address. The backend recipient input
// still rejects blank names, so an email-only entry is saved as a direct
// email (it still reaches the audience) and only named entries keep metadata.
export const toNamedRecipientInputs = (
  recipients: EmailAdditionalRecipient[]
): EmailAdditionalRecipient[] =>
  recipients
    .map((recipient) => ({
      email: recipient.email.trim().toLowerCase(),
      firstName: recipient.firstName.trim(),
      lastName: recipient.lastName.trim(),
      companyName: recipient.companyName?.trim() || undefined,
    }))
    .filter((recipient) => recipient.firstName && recipient.lastName);

// Saved selection values shown as checked categories in the audience popup.
export const getGroupCategoryIds = (group?: RecipientGroup): string[] => [
  ...(group?.selection?.criteriaIds || []),
  ...(group?.selection?.governanceGroupIds || []).map((id) => `governance:${id}`),
  ...(group?.selection?.sourceKinds?.includes(CmpNewsletterRecipientSourceKindEnum.ProspectMembers) ? ["status:PROSPECT"] : []),
];

const recipientDisplayName = (recipient: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) =>
  [recipient.firstName, recipient.lastName].filter((part) => part?.trim()).join(" ") ||
  recipient.email ||
  "";

type CreateCustomAudienceModalProps = {
  initialGroup?: RecipientGroup;
  memberTypeOptions: PopupCategoryOption[];
  industryOptions: PopupCategoryOption[];
  memberOptions: EmailAudienceMemberOption[];
  governance: ChamberMemberProGovernanceSettingsReturn | undefined;
  isLoading: boolean;
  loadError: string;
  onRetry: () => void;
  onClose: () => void;
  onCreate: (payload: CreateCustomAudiencePayload) => Promise<void>;
};

export const CreateCustomAudienceModal = ({
  initialGroup,
  memberTypeOptions,
  industryOptions,
  memberOptions,
  governance,
  isLoading,
  loadError,
  onRetry,
  onClose,
  onCreate,
}: CreateCustomAudienceModalProps) => {
  const [audienceName, setAudienceName] = useState(initialGroup?.name || "");
  const initialCategoryIds = getGroupCategoryIds(initialGroup);
  const [selectedCategoryIds] = useState<string[]>(initialCategoryIds);
  const [additionalRecipients, setAdditionalRecipients] = useState<
    EmailAdditionalRecipient[]
  >((initialGroup?.selection?.additionalRecipients || []).map(recipient => ({ ...recipient })));
  const [recipientEntry, setRecipientEntry] =
    useState<EmailAdditionalRecipient>({
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
    });
  const [recipientErrors, setRecipientErrors] = useState<
    Partial<Record<keyof EmailAdditionalRecipient, string>>
  >({});
  const [nameError, setNameError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const titleId = useId();
  const initialValues = useRef({ audienceName, selectedCategoryIds, additionalRecipients });
  const isDirty =
    audienceName !== initialValues.current.audienceName ||
    JSON.stringify(selectedCategoryIds) !== JSON.stringify(initialValues.current.selectedCategoryIds) ||
    JSON.stringify(additionalRecipients) !== JSON.stringify(initialValues.current.additionalRecipients) ||
    Object.values(recipientEntry).some(Boolean);
  const requestClose = () => {
    if (isSaving) return;
    if (isDirty) setShowDiscardConfirm(true);
    else onClose();
  };

  const membershipTypeCategoryOptions = memberTypeOptions
    .filter((option) => !isTierMembershipModel(option))
    .map((option) => ({ id: `type:${option.id}`, label: option.label }));
  const tierCategoryOptions = memberTypeOptions
    .filter(isTierMembershipModel)
    .map((option) => ({ id: `type:${option.id}`, label: option.label }));
  const industryCategoryOptions = industryOptions.map((option) => ({
    id: `industry:${option.id}`,
    label: option.label,
  }));
  const governanceCategories = [
    { title: "Board of Directors", groups: governance?.boards || [] },
    { title: "Committees", groups: governance?.committees || [] },
    { title: "Task Forces", groups: governance?.taskForces || [] },
  ].map(({ title, groups }) => ({
    title,
    options: groups.map((group) => ({
      id: `governance:${group.id}`,
      label: group.name,
    })),
  }));
  // Claude Design (16): a list is a name plus recipients. F5 (Moeen, Oct 12
  // 2026): a list saved earlier with audience categories keeps them; Edit
  // shows them read only and saves them unchanged.
  const savedCategoryLabelById = new Map<string, string>([
    ...popupMemberStatusOptions, ...membershipTypeCategoryOptions, ...tierCategoryOptions, ...industryCategoryOptions,
    ...popupMemberSinceOptions, ...popupRenewalStatusOptions, ...governanceCategories.flatMap((group) => group.options),
  ].map((option): [string, string] => [option.id, option.label]));
  const savedCategoryLabels = initialCategoryIds.map((id) => savedCategoryLabelById.get(id) || (isLoading ? "Loading..." : "Category no longer available"));
  const createDisabled = isSaving;

  const saveRecipientEntry = () => {
    const entry = {
      firstName: recipientEntry.firstName.trim(),
      lastName: recipientEntry.lastName.trim(),
      email: recipientEntry.email.trim().toLowerCase(),
      companyName: recipientEntry.companyName?.trim() || "",
    };
    const errors: Partial<Record<keyof EmailAdditionalRecipient, string>> = {};
    if (!isPlausibleEmail(entry.email))
      errors.email = "A valid Email Address is required";
    else if (
      additionalRecipients.some((recipient) => recipient.email === entry.email)
    )
      errors.email = `${entry.email} is already in this audience.`;
    setRecipientErrors(errors);
    if (Object.keys(errors).length) return;
    setAdditionalRecipients((recipients) => [...recipients, entry]);
    setRecipientEntry({
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
    });
    setFormError("");
  };

  const handleCreateAudience = async () => {
    const trimmedName = audienceName.trim();
    const pendingEntry = Object.values(recipientEntry).some((value) => value?.trim());
    setNameError(!trimmedName ? "Cannot create \u2014 you must give this audience a name."
      : !initialGroup && !additionalRecipients.length && !pendingEntry ? "Cannot create \u2014 you must add at least one recipient." : "");
    setFormError("");
    if (!trimmedName || createDisabled) return;
    if (!initialGroup && !additionalRecipients.length && !pendingEntry) return;
    if (Object.values(recipientEntry).some((value) => value?.trim())) {
      setFormError(
        `Select Save to add this recipient, then select ${initialGroup ? "Save Changes" : "Create Audience"}.`
      );
      return;
    }
    const currentCategoryIds = initialCategoryIds;
    if (!currentCategoryIds.length && !additionalRecipients.length && !initialGroup?.selection?.memberIds?.length && !initialGroup?.selection?.eventIds?.length && !initialGroup?.selection?.directEmails?.length && !initialGroup?.selection?.sourceKinds?.length) {
      setFormError(
        "Add at least one recipient before you save this audience."
      );
      return;
    }
    setIsSaving(true);
    try {
      await onCreate(
        buildCustomAudiencePayload({
          name: trimmedName,
          initialGroup,
          selectedCategoryIds: currentCategoryIds,
          memberOptions,
          additionalRecipients,
        })
      );
    } catch (error) {
      setFormError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("save this custom audience")
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <ModalShell
        open
        enableKeyboardFocus
        onClose={requestClose}
        labelledById={titleId}
        ariaBusy={isSaving || isLoading}
        showCloseButton={false}
        overlayClassName="bg-[#0A0A0A]/45"
        panelClassName="relative max-w-[900px] !max-h-[92vh] !overflow-hidden !rounded-[14px] !border-[#ECECEC] !p-0 !shadow-[0_18px_48px_rgba(0,0,0,0.18)]"
        bodyClassName="flex min-h-0 flex-col"
      >
        <div
          className="flex max-h-[92vh] min-h-0 flex-col font-['DM_Sans']"
        >
          <div className="relative shrink-0 border-b border-[#ECECEC] px-5 pb-[18px] pt-[26px] md:px-8">
            <button
              type="button"
              aria-label="Close"
              disabled={isSaving}
              className="absolute right-[14px] top-[14px] flex h-7 w-7 items-center justify-center rounded-full bg-[#F21B1B] text-white disabled:opacity-60"
              onClick={requestClose}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </button>
            <h2
              id={titleId}
              className="pr-7 text-[18px] font-bold text-[#0A0A0A]"
            >
              {initialGroup ? "Edit Custom Audience" : "Create NEW Custom Audience"}
            </h2>
            <p className="mt-1 text-[14px] text-[#0A0A0A]">
              Name the audience and add the recipients you want to include.
              Recipients can be deleted.
            </p>
          </div>
          <div className="flex min-h-0 flex-col gap-[26px] overflow-y-auto px-5 py-[26px] md:px-8">
            <div>
              <SectionHeading
                compact
                index={1}
                title={<>Name Your Audience <span className="text-red-600">*</span></>}
                description="Give this audience a name you will recognize when selecting it for a future email."
              />
              <label className="sr-only" htmlFor={`${titleId}-name`}>
                Audience name
              </label>
              <input
                autoFocus
                id={`${titleId}-name`}
                className={`${popupInputClassName} !px-[14px] md:ml-[38px] md:!w-[calc(100%_-_38px)] ${nameError && !audienceName.trim() ? "!border-[#E24C4C]" : ""}`}
                placeholder="e.g., Q3 Newsletter Recipients"
                value={audienceName}
                disabled={isSaving}
                aria-invalid={!!nameError && !audienceName.trim()}
                aria-describedby={nameError && !audienceName.trim() ? `${titleId}-name-error` : undefined}
                onChange={(event) => setAudienceName(event.target.value)}
              />
            </div>
            {initialCategoryIds.length > 0 && (
              <div className="rounded-lg border border-[#ECECEC] bg-[#F7F7F8] px-4 py-[14px] md:ml-[38px]">
                <p className="text-[14px] font-semibold text-[#0A0A0A]">Saved Audience Categories</p>
                <p className="mt-1 text-[13px] leading-[1.45] text-[#727272]">
                  This list was created with audience categories. Members in these categories still receive emails sent to this list. The categories cannot be changed here.
                </p>
                {loadError ? (
                  <p role="alert" className="mt-2 text-[13px] text-red-600">
                    {loadError}{" "}
                    <button type="button" onClick={onRetry} className="underline">Try again</button>
                  </p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  {savedCategoryLabels.map((label, index) => (
                    <span key={`${initialCategoryIds[index]}`} className="rounded-full border border-[#ECECEC] bg-white px-3 py-1 text-[13px] text-[#0A0A0A]">{label}</span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <SectionHeading
                compact
                index={2}
                title={<>Add Additional Recipients <span className="text-red-600">*</span></>}
                description="Add individual recipients that are not part of your existing lists."
              />
              <div className="grid grid-cols-1 gap-[14px] md:ml-[38px] md:grid-cols-2">
                {(
                  [
                    {
                      field: "firstName",
                      label: "First Name",
                      required: false,
                      maxLength: 100,
                    },
                    {
                      field: "lastName",
                      label: "Last Name",
                      required: false,
                      maxLength: 100,
                    },
                    {
                      field: "email",
                      label: "Email Address",
                      required: true,
                      maxLength: 254,
                    },
                    {
                      field: "companyName",
                      label: "Company Name",
                      required: false,
                      maxLength: 200,
                    },
                  ] as const
                ).map(({ field, label, required, maxLength }) => (
                  <label
                    key={field}
                    className="flex min-w-0 flex-col gap-1.5 text-[14px] font-semibold text-[#0A0A0A]"
                  >
                    <span>
                      {label}
                      {required ? (
                        <span className="text-[#E24C4C]"> *</span>
                      ) : null}
                    </span>
                    <input
                      type={field === "email" ? "email" : "text"}
                      className={popupInputClassName}
                      placeholder={`Enter ${label}`}
                      maxLength={maxLength}
                      value={recipientEntry[field] || ""}
                      disabled={isSaving}
                      aria-invalid={!!recipientErrors[field]}
                      onChange={(event) =>
                        setRecipientEntry((entry) => ({
                          ...entry,
                          [field]: event.target.value,
                        }))
                      }
                    />
                    {recipientErrors[field] ? (
                      <span
                        role="alert"
                        className="text-[12px] font-normal text-[#E24C4C]"
                      >
                        {recipientErrors[field]}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
              <button
                type="button"
                onClick={saveRecipientEntry}
                disabled={isSaving}
                className={`${primaryButtonClassName} mt-[14px] !min-h-[43px] !rounded-lg !border-0 !px-5 !py-[11px] !text-[14px] !font-semibold md:ml-[38px]`}
              >
                <span className="text-[16px] leading-none">Save</span>
              </button>
              <div className="mt-[14px] flex flex-col gap-2 md:ml-[38px]">
                {additionalRecipients.map((recipient) => (
                  <div
                    key={recipient.email}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#ECECEC] bg-[#F7F7F8] px-[14px] py-2.5"
                  >
                    <div className="min-w-0 text-[14px]">
                      <p className="font-semibold text-[#0A0A0A]">
                        {recipientDisplayName(recipient)}
                      </p>
                      <p className="truncate text-[#727272]">
                        {[recipient.email, recipient.companyName]
                          .filter(Boolean)
                          .join(" \u2022 ")}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${recipient.email}`}
                      disabled={isSaving}
                      onClick={() =>
                        setAdditionalRecipients((recipients) =>
                          recipients.filter(
                            (item) => item.email !== recipient.email
                          )
                        )
                      }
                      className="shrink-0 text-[#9E9E9E]"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            {formError ? (
              <p role="alert" className="text-[14px] text-red-600">
                {formError}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-[14px] border-t border-[#ECECEC] bg-[#F7F7F8] px-5 py-[18px] md:px-8">
            {nameError ? (
              <p
                id={`${titleId}-name-error`}
                role="alert"
                className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-[#E24C4C]"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#E24C4C" strokeWidth="2" strokeLinecap="round" className="shrink-0" aria-hidden="true">
                  <circle cx="12" cy="12" r="9.5" />
                  <path d="M12 7.5v6M12 16.6v.2" />
                </svg>
                {nameError}
              </p>
            ) : null}
            <div className="ml-auto flex flex-wrap justify-end gap-[14px]">
            <button
              type="button"
              className={`${secondaryButtonClassName} !min-h-[43px] !rounded-lg !border-[#9E9E9E] !px-6 !py-[11px] !text-[14px] !font-semibold !leading-[normal] !text-[#0A0A0A]`}
              onClick={requestClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${primaryButtonClassName} !min-h-[43px] !rounded-lg !border-0 !px-6 !py-[11px] !text-[14px] !font-semibold !leading-[normal]`}
              onClick={handleCreateAudience}
              disabled={createDisabled}
            >
              {isSaving ? <CircularProgress color="inherit" size={18} /> : null}
              {initialGroup ? "Save Changes" : "Create Audience"}
            </button>
            </div>
          </div>
        </div>
      </ModalShell>
      <ConfirmDialog
        open={showDiscardConfirm}
        constrainContentWidth
        variant="confirm"
        title="Discard audience changes?"
        message="Your custom audience has not been saved."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        zIndex={10000}
        onConfirm={onClose}
        onClose={() => setShowDiscardConfirm(false)}
      />
    </>
  );
};

type PreviewRecipientRow = NonNullable<RecipientPreviewSummary["recipients"]>[number];

type RecipientsListModalProps = {
  /** "estimated" is this email's audience; "customList" is one saved list. */
  variant: "estimated" | "customList";
  title: string;
  description: string;
  preview?: RecipientPreviewSummary | null;
  previewLoading: boolean;
  previewError: string;
  email: EmailFormState;
  /** True when this email cannot be edited (busy or already sent). */
  disabled: boolean;
  /** True when recipients cannot be added to this audience. */
  addDisabled?: boolean;
  /** Addresses already in the audience outside the preview rows. */
  knownEmails?: string[];
  onClose: () => void;
  updateEmailField: UpdateEmailField;
  onAddRecipients: (recipients: EmailAdditionalRecipient[]) => Promise<void> | void;
  onRemoveFromList?: (recipientKey: string) => Promise<void>;
};

type RecipientsListResult = { message: string; tone: "success" | "error" };

const previewRowKey = (row: PreviewRecipientRow) =>
  (row.email || row.intendedEmail || "").trim().toLowerCase();

const previewRowName = (row: PreviewRecipientRow) =>
  recipientDisplayName({ firstName: row.firstName, lastName: row.lastName, email: row.email || row.intendedEmail });

const recipientsAddedMessage = (count: number) =>
  `${count} ${count === 1 ? "recipient has" : "recipients have"} been added to the recipients list!`;

// Claude Design recipients pop-up grid: 44px | 1fr | 1.4fr | 1.1fr | (.9fr
// Category) | 72px. The table uses fixed layout, where Chrome scales
// percentage columns that sum to 100% into the width left after the 44px and
// 72px columns, which gives the same split as the design fr tracks.
const RECIPIENT_COLUMN_WIDTHS = {
  customList: { name: "28.5714%", email: "40%", company: "31.4286%" },
  estimated: { name: "22.7273%", email: "31.8182%", company: "25%", category: "20.4545%" },
} as const;

const RecipientsListModal = ({
  variant,
  title,
  description,
  preview,
  previewLoading,
  previewError,
  email,
  disabled,
  addDisabled = false,
  knownEmails = [],
  onClose,
  updateEmailField,
  onAddRecipients,
  onRemoveFromList,
}: RecipientsListModalProps) => {
  const titleId = useId();
  const isCustomList = variant === "customList";
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<PreviewRecipientRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [result, setResult] = useState<RecipientsListResult | null>(null);
  const [sort, setSort] = useState<EmailManageSort>({ column: null, direction: "asc" });
  // A changed selection clears the parent's preview before the debounced read.
  // Missing rows are incomplete data, not an empty audience or a duplicate list.
  const previewReady = !previewLoading && !previewError && !!preview && Array.isArray(preview.recipients);
  const pending = !previewReady && !previewError;
  // S12: a list View keeps the contacts a sandbox chamber's send skips.
  const rows = (previewReady ? preview.recipients || [] : []).filter(
    (row) => row.status === "VALID" || row.skippedReason === "EXCLUDED_BY_USER" ||
      (isCustomList && !!row.skippedBySandbox)
  );
  const recipientCount = previewReady
    ? isCustomList ? getCustomListContactCount(preview) : preview.uniqueValidRecipientCount
    : null;
  const sandboxNotice = isCustomList && previewReady ? preview.sandboxNotice : null;
  const excluded = new Set(email.excludedRecipientEmails);
  const allSelected = rows.length > 0 && rows.every((row) => !excluded.has(previewRowKey(row)));
  // The checkbox column always means "include in this email", in both views,
  // and saves with the draft as the selection's excluded emails.
  const toggleRecipients = (addresses: string[], include: boolean) => {
    if (disabled || !previewReady) return;
    const next = new Set(email.excludedRecipientEmails);
    addresses.forEach((address) => include ? next.delete(address) : next.add(address));
    updateEmailField("excludedRecipientEmails", Array.from(next));
  };
  const filtered = rows.filter((row) =>
    [row.firstName, row.lastName, row.companyName, row.email]
      .join(" ")
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );
  const sorted = sort.column ? [...filtered].sort((first, second) => {
    const value = (row: typeof first) => sort.column === "name"
      ? [row.firstName, row.lastName].filter(Boolean).join(" ") || "Not provided"
      : sort.column === "company" ? row.companyName || "Not provided" : row.sourceLabel;
    return value(first).toLowerCase().localeCompare(value(second).toLowerCase()) * (sort.direction === "asc" ? 1 : -1);
  }) : filtered;
  const pageCount = Math.max(1, Math.ceil(filtered.length / 10));
  const currentPage = Math.min(page, pageCount);
  const removeLabel = isCustomList ? "Remove from this list" : "Remove from this email";
  const removalActionsDisabled = !previewReady || removing || (!isCustomList && disabled);
  const pendingRemovalName = pendingRemoval ? previewRowName(pendingRemoval) : "";

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    const recipientKey = previewRowKey(pendingRemoval);
    if (!isCustomList) {
      toggleRecipients([recipientKey], false);
      setPendingRemoval(null);
      setResult({ message: `${pendingRemovalName} has been removed from this email.`, tone: "success" });
      return;
    }
    setRemoving(true);
    try {
      if (!onRemoveFromList) throw new Error(CUSTOM_AUDIENCE_RECIPIENT_REMOVAL_UNAVAILABLE_MESSAGE);
      await onRemoveFromList(recipientKey);
      setResult({ message: `${pendingRemovalName} has been removed from "${title}".`, tone: "success" });
    } catch (error) {
      setResult({
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(`remove ${pendingRemovalName} from "${title}"`)
        ),
        tone: "error",
      });
    } finally {
      setRemoving(false);
      setPendingRemoval(null);
    }
  };

  const recipientColumnWidths = RECIPIENT_COLUMN_WIDTHS[variant];
  const columns: Array<DataTableColumn<PreviewRecipientRow>> = [
    {
      key: "name",
      header: "Recipient",
      sortable: true,
      width: recipientColumnWidths.name,
      render: (row) =>
        [row.firstName, row.lastName].filter(Boolean).join(" ") ||
        "Not provided",
    },
    {
      key: "email",
      header: "Email Address",
      width: recipientColumnWidths.email,
      render: (row) => row.email || row.intendedEmail,
    },
    {
      key: "company",
      header: "Company",
      sortable: true,
      width: recipientColumnWidths.company,
      render: (row) => row.companyName || "Not provided",
    },
    ...(isCustomList ? [] : [{
      key: "category",
      header: "Category",
      sortable: true,
      width: RECIPIENT_COLUMN_WIDTHS.estimated.category,
      render: (row: PreviewRecipientRow) => row.sourceLabel,
    }]),
    {
      key: "actions",
      header: "Actions",
      width: "72px",
      render: (row) => (
        <button
          type="button"
          title={removeLabel}
          aria-label={removeLabel}
          disabled={removalActionsDisabled || (!isCustomList && excluded.has(previewRowKey(row)))}
          onClick={() => setPendingRemoval(row)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[#FDECEC] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#F21B1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      ),
    },
  ];

  return (
    <>
      <ModalShell
        open
        enableKeyboardFocus
        overlayClassName={adding ? "invisible" : undefined}
        onClose={onClose}
        labelledById={titleId}
        ariaBusy={pending || removing}
        showCloseButton={false}
        panelClassName="relative !w-[min(900px,92vw)] max-w-[900px] !max-h-[92vh] !rounded-[14px] !border-[#ECECEC] !p-0"
      >
        <EmailDesignScope>
          <div className="flex max-h-[calc(92vh-2px)] flex-col">
          <div className="relative shrink-0 border-b border-[#ECECEC] px-5 pb-4 pt-6 md:px-8">
            <button
              type="button"
              aria-label="Close recipients list"
              onClick={onClose}
              className="absolute right-[14px] top-[14px] flex h-7 w-7 items-center justify-center rounded-full bg-[#F21B1B] text-white"
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </button>
            <h2 id={titleId} className="pr-7 text-[18px] font-bold">
              {title}
            </h2>
            <p className="mt-1 text-[14px] font-semibold" aria-live="polite">
              {previewError ? "Recipient count unavailable." : recipientCount !== null ? `${formatNumber(recipientCount)} recipients.` : "Calculating recipients..."}
            </p>
            {description && <p className="mt-1 text-[14px] leading-[1.5]">{description}</p>}
            {/* Not in design: Sand Bay (sandbox) only, the backend note (Monday 13390105883). */}
            {sandboxNotice && <p role="note" className="mt-1 text-[13px] leading-[1.5] text-[#727272]">{sandboxNotice}</p>}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 bg-[#F7F7F8] px-5 py-4 md:px-8">
            <div className="relative w-full max-w-[360px]">
            <SearchIcon aria-hidden="true" sx={{ fontSize: 15 }} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
            <input
              aria-label="Search recipients"
              className={`${inputClassName} !h-[41px] !py-2.5 !pl-9 !font-normal !leading-[normal]`}
              placeholder="Search by name, email, or company"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            </div>
            <button
              type="button"
              className={`${primaryButtonClassName} shrink-0 !min-h-[41px] !border-0 !px-5 !py-[11px] !leading-[normal]`}
              disabled={addDisabled || !previewReady}
              onClick={() => setAdding(true)}
            >
              <PersonAddOutlinedIcon sx={{ fontSize: 16 }} />
              Add Additional Recipients
            </button>
          </div>
          <div className="min-h-0 overflow-y-auto">
            {previewError && <p role="alert" className="px-5 py-3 text-[14px] text-[#E24C4C] md:px-8">{previewError}</p>}
            <DataTable
              ariaLabel="Email recipients"
              keepFeedbackInViewport
              containerClassName="w-full overflow-x-auto bg-white"
              rows={sorted.slice((currentPage - 1) * 10, currentPage * 10)}
              getRowKey={previewRowKey}
              selection={{
                disabled: disabled || !previewReady,
                allSelected,
                someSelected: rows.some((row) => !excluded.has(previewRowKey(row))),
                isRowSelected: (row) => !excluded.has(previewRowKey(row)),
                onToggleRow: (row) => toggleRecipients([previewRowKey(row)], excluded.has(previewRowKey(row))),
                onToggleAll: () => toggleRecipients(rows.map(previewRowKey), !allSelected),
                headerAriaLabel: "Select or unselect all recipients",
                getRowAriaLabel: (row) => `Include ${previewRowKey(row)}`,
                checkboxSx: { p: 0 },
              }}
              showLoadingRow={pending}
              loadingRowContent={<div role="status" className="py-3 text-center">Loading recipients...</div>}
              showEmptyRow={previewReady && !filtered.length}
              emptyRowContent={
                search
                  ? "No recipients match your search."
                  : "No valid recipients."
              }
              tableSx={{
                minWidth: 760,
                tableLayout: "fixed",
                width: "100%",
                "&&& .MuiTableCell-root": {
                  padding: "12px 14px",
                  borderColor: "#F0F0F0",
                  fontSize: "14px !important",
                  lineHeight: "normal",
                  color: "#0A0A0A",
                  overflowWrap: "anywhere",
                },
                // Design checkbox column: 44px wide, padding 12px 0.
                "&&& .MuiTableCell-paddingCheckbox": { width: 44, padding: "12px 0" },
                "&&& .MuiTableBody-root .MuiTableCell-root:last-of-type": { padding: "8px 14px" },
                // Design header: 12px/600 uppercase .04em, one line per label.
                "&&& .MuiTableHead-root .MuiTableCell-root": {
                  fontSize: "12px !important",
                  fontWeight: 600,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  backgroundColor: "#F7F7F8",
                  borderColor: "#ECECEC",
                  whiteSpace: "nowrap",
                  overflowWrap: "normal",
                },
              }}
              sort={{ column: sort.column || "email", direction: sort.direction, onRequestSort: (column) => { setSort((current) => nextEmailManageSort(current, column)); setPage(1); } }}
              renderSortLabel={({ column, label, active, direction, onRequestSort }) => <button type="button" disabled={!previewReady} onClick={() => onRequestSort(column)} className="inline-flex items-center gap-[6px] text-left font-semibold uppercase tracking-[.04em]" style={{ fontSize: "inherit", lineHeight: "inherit" }} aria-label={`Sort by ${String(label)}`}>
                <span>{label}</span><svg width="9" height="14" viewBox="0 0 9 14" aria-hidden="true" className="shrink-0"><path d="M4.5 1 8 5H1z" fill={active && direction === "asc" ? "#E1A731" : "#C4C7CC"} /><path d="M4.5 13 1 9h7z" fill={active && direction === "desc" ? "#E1A731" : "#C4C7CC"} /></svg>
              </button>}
              columns={columns}
            />
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-t border-[#ECECEC] bg-[#F7F7F8] px-5 py-[14px] text-[14px] md:px-8">
            <span>{recipientCount !== null ? `${formatNumber(recipientCount)} recipients` : "Recipient count unavailable"}</span>
            <Pagination count={pageCount} page={currentPage} onChange={(_event, value) => setPage(value)} disabled={!previewReady}
              getItemAriaLabel={(type, value, selected) => type === "page" ? `${selected ? "Current recipients page" : "Go to recipients page"} ${value}` : type === "previous" ? "Previous recipients page" : type === "next" ? "Next recipients page" : `Recipients ${type}`}
              sx={{ "& .MuiPagination-ul": { gap: "6px", flexWrap: "wrap" }, "&& .MuiPaginationItem-root": { width: 30, minWidth: 30, height: 30, margin: 0, padding: 0, border: 0, borderRadius: "6px", fontFamily: "inherit", fontSize: 14, color: "#0A0A0A" }, "&& .Mui-selected": { backgroundColor: "#FDF8EE", color: "#E1A731", fontWeight: 700 }, "&& .MuiPaginationItem-previousNext": { color: "#727272" }, "& .MuiPaginationItem-icon": { fontSize: 15 } }} />
          </div>
          </div>
        </EmailDesignScope>
      </ModalShell>
      {adding && (
        <AdditionalRecipientsModal
          disabled={addDisabled || !previewReady}
          existingEmails={Array.from(new Set([
            ...rows.map((row) => row.email || row.intendedEmail || ""),
            ...knownEmails,
          ].map((address) => address.trim().toLowerCase()).filter(Boolean)))}
          onClose={() => setAdding(false)}
          onAdd={async (recipients) => {
            if (addDisabled || !previewReady) return;
            await onAddRecipients(recipients);
            setAdding(false);
            setResult({ message: recipientsAddedMessage(recipients.length), tone: "success" });
          }}
        />
      )}
      <ConfirmDialog
        open={!!pendingRemoval}
        variant="destructive"
        title="Remove Recipient"
        message={isCustomList
          ? `Remove ${pendingRemovalName} from “${title}”? They stay in your member records and any other audience list.`
          : `Remove ${pendingRemovalName} from this email? They stay in your member records and audience lists.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        loading={removing}
        zIndex={10000}
        onConfirm={() => void confirmRemoval()}
        onClose={() => setPendingRemoval(null)}
      />
      <ActionSuccessDialog
        message={result?.message}
        tone={result?.tone}
        onClose={() => setResult(null)}
      />
    </>
  );
};

const AdditionalRecipientsModal = ({
  existingEmails,
  disabled,
  onClose,
  onAdd,
}: {
  existingEmails: string[];
  disabled: boolean;
  onClose: () => void;
  onAdd: (recipients: EmailAdditionalRecipient[]) => Promise<void>;
}) => {
  const titleId = useId();
  const [entry, setEntry] = useState<EmailAdditionalRecipient>({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
  });
  const [recipients, setRecipients] = useState<EmailAdditionalRecipient[]>([]);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof EmailAdditionalRecipient, string>>>({});
  const [discardOpen, setDiscardOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const locked = disabled || saving;
  const dirty =
    recipients.length > 0 ||
    Object.values(entry).some((value) => value?.trim());
  const requestClose = () => {
    if (saving) return;
    if (dirty) setDiscardOpen(true);
    else onClose();
  };
  const addEntry = () => {
    if (locked) return;
    const nextErrors: Partial<Record<keyof EmailAdditionalRecipient, string>> = {};
    if (!isPlausibleEmail(entry.email.trim())) nextErrors.email = "A valid Email Address is required";
    setFieldErrors(nextErrors);
    setError("");
    if (Object.keys(nextErrors).length) return;
    const address = entry.email.trim().toLowerCase();
    if (
      existingEmails.includes(address) ||
      recipients.some((recipient) => recipient.email === address)
    ) {
      setError(`${address} is already in this audience.`);
      return;
    }
    setRecipients([
      ...recipients,
      {
        email: address,
        firstName: entry.firstName.trim(),
        lastName: entry.lastName.trim(),
        companyName: entry.companyName?.trim() || "",
      },
    ]);
    setEntry({ firstName: "", lastName: "", email: "", companyName: "" });
    setError("");
  };
  const submit = async () => {
    if (locked) return;
    if (Object.values(entry).some((value) => value?.trim())) {
      setError("Select Add Recipient to add this person first.");
      return;
    }
    if (!recipients.length) {
      setError("No recipients have been added.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onAdd(recipients);
    } catch (addError) {
      setError(getUserFacingErrorText(addError, buildStaffErrorFallback("add these recipients")));
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <ModalShell
        open
        enableKeyboardFocus
        onClose={requestClose}
        labelledById={titleId}
        ariaBusy={saving}
        showCloseButton={false}
        panelClassName="relative max-w-[700px] !max-h-[92vh] !rounded-[14px] !border-[#ECECEC] !p-0"
      >
        <EmailDesignScope>
          <div className="flex max-h-[calc(92vh-2px)] flex-col">
          <div className="relative shrink-0 border-b border-[#ECECEC] px-5 pb-4 pt-6 md:px-8">
            <button
              type="button"
              aria-label="Close additional recipients"
              disabled={saving}
              onClick={requestClose}
              className="absolute right-[14px] top-[14px] flex h-7 w-7 items-center justify-center rounded-full bg-[#F21B1B] text-white"
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </button>
            <h2 id={titleId} className="pr-7 text-[18px] font-bold">
              Add Additional Recipients
            </h2>
            <p className="mt-1 text-[14px]">
              Add recipients who are not already included in the selected
              audience.
            </p>
          </div>
          <div className="flex min-h-0 flex-col gap-[18px] overflow-y-auto px-5 py-6 md:px-8">
            <div>
            <h3 className="text-[16px] font-semibold">
              Recipient Details
            </h3>
            <p className="mt-1 text-[14px]">
              Enter the recipient&apos;s details and select Add Recipient.
            </p>
            </div>
            <div className="grid grid-cols-1 gap-[14px] md:grid-cols-2">
              {(
                [
                  { key: "firstName", label: "First Name" },
                  { key: "lastName", label: "Last Name" },
                  { key: "email", label: "Email Address" },
                  { key: "companyName", label: "Company Name" },
                ] as const
              ).map(({ key, label }) => (
                <label key={key} className="text-[14px] font-semibold">
                  {label}
                  {key === "email" && (
                    <span className="text-[#E24C4C]"> *</span>
                  )}
                  <input
                    type={key === "email" ? "email" : "text"}
                    className={`${inputClassName} mt-1.5 !h-[41px] !px-3 !py-2.5 !font-normal !leading-[normal] ${fieldErrors[key] ? "!border-[#E24C4C]" : ""}`}
                    placeholder={`Enter ${label}`}
                    value={entry[key] || ""}
                    disabled={locked}
                    aria-invalid={!!fieldErrors[key]}
                    aria-describedby={fieldErrors[key] ? `${titleId}-${key}-error` : undefined}
                    maxLength={
                      key === "email" ? 254 : key === "companyName" ? 200 : 100
                    }
                    onChange={(event) =>
                      setEntry({ ...entry, [key]: event.target.value })
                    }
                  />
                  {fieldErrors[key] && <span id={`${titleId}-${key}-error`} role="alert" className="mt-1.5 block text-[12px] font-normal text-[#E24C4C]">{fieldErrors[key]}</span>}
                </label>
              ))}
            </div>
            <button
              type="button"
              className={`${primaryButtonClassName} w-fit !min-h-[41px] !border-0 !px-5 !py-[11px] !leading-[normal]`}
              disabled={locked}
              onClick={addEntry}
            >
              <span className="text-[16px] leading-none">+</span>{" "}Add Recipient
            </button>
            {recipients.length > 0 && <div className="space-y-2">
              {recipients.map((recipient) => (
                <div
                  key={recipient.email}
                  className="flex items-center justify-between gap-3 rounded-lg border border-[#ECECEC] bg-[#F7F7F8] px-[14px] py-2.5 text-[14px]"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <p className="font-semibold">
                      {recipientDisplayName(recipient)}
                    </p>
                    <p className="break-words text-[#727272]">{[recipient.email, recipient.companyName].filter(Boolean).join(" • ")}</p>
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${recipient.email}`}
                    disabled={locked}
                    className="flex h-8 w-8 shrink-0 items-center justify-center"
                    onClick={() =>
                      setRecipients(
                        recipients.filter(
                          (row) => row.email !== recipient.email
                        )
                      )
                    }
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-t border-[#ECECEC] bg-[#F7F7F8] px-5 py-4 md:px-8">
            <div>
            <p className="text-[14px]">
              {recipients.length} {recipients.length === 1 ? "recipient" : "recipients"} will be added
            </p>
            {error && <p role="alert" className="mt-1 text-[13px] text-[#E24C4C]">{error}</p>}
            {disabled && <p role="status" className="mt-1 text-[13px]">Wait for the recipient preview before adding entries.</p>}
            </div>
            <div className="flex w-full flex-col justify-end gap-[14px] sm:w-auto sm:flex-row">
              <button
                type="button"
                className={`${secondaryButtonClassName} !px-6 !py-[11px] !leading-[normal]`}
                disabled={saving}
                onClick={requestClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${primaryButtonClassName} !min-h-[41px] !border-0 !px-6 !py-[11px] !leading-[normal]`}
                disabled={locked}
                onClick={() => void submit()}
              >
                {saving ? <CircularProgress color="inherit" size={16} /> : null}
                Add Additional Recipients
              </button>
            </div>
          </div>
          </div>
        </EmailDesignScope>
      </ModalShell>
      <ConfirmDialog
        open={discardOpen}
        constrainContentWidth
        variant="confirm"
        title="Discard recipient changes?"
        message="The additional recipients have not been added."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        zIndex={10000}
        onClose={() => setDiscardOpen(false)}
        onConfirm={onClose}
      />
    </>
  );
};

const StepOneAudience = ({
  email,
  recipientGroups,
  memberTypeOptions,
  audienceFilters,
  intendedRecipientCount,
  isAudiencePreviewLoading,
  audiencePreviewError,
  isBusy,
  isSentRecord,
  toggleRecipientSource,
  toggleRecipientGroup,
  updateAudienceFilter,
  clearAllFilters,
  updateEmailField,
  recipientPreview,
  onRefreshRecipients,
  archiveRecipientGroup,
}: StepOneAudienceProps) => {
  const client = useApolloClient();
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [isAudiencePopupOpen, setIsAudiencePopupOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RecipientGroup | undefined>();
  const [viewingGroup, setViewingGroup] = useState<RecipientGroup | null>(null);
  const [groupPreview, setGroupPreview] = useState<RecipientPreviewSummary | null>(null);
  const [groupPreviewLoading, setGroupPreviewLoading] = useState(false);
  const [groupPreviewError, setGroupPreviewError] = useState("");
  const groupPreviewRequest = useRef(0);
  // The saved list count is stored when the list is saved, so it can be out
  // of date. When the list pop-up loads its live preview, the table row shows
  // that same count so the table and the pop-up agree.
  const [livePreviewGroupCounts, setLivePreviewGroupCounts] = useState<Record<string, number>>({});
  const clearLivePreviewGroupCount = (groupId: string) =>
    setLivePreviewGroupCounts(({ [groupId]: _removed, ...rest }) => rest);
  const viewGroup = async (group: RecipientGroup) => {
    const request = ++groupPreviewRequest.current;
    setViewingGroup(group);
    setGroupPreview(null);
    setGroupPreviewError("");
    setGroupPreviewLoading(true);
    try {
      const result = await client.query({
        query: PreviewCmpNewsletterRecipientsDocument,
        variables: { input: { chamberId, recipientSelection: { groupIds: [group.id] } } },
        fetchPolicy: "network-only",
        context: EMAIL_OPERATION_ERROR_CONTEXT,
      });
      if (request === groupPreviewRequest.current) {
        const preview = previewQueryToSummary(result.data.previewCmpNewsletterRecipients);
        setGroupPreview(preview);
        // S12: the CONTACTS column keeps the list count (sandbox-skipped contacts included).
        const liveCount = preview ? getCustomListContactCount(preview) : undefined;
        if (typeof liveCount === "number" && Number.isInteger(liveCount) && liveCount >= 0) {
          setLivePreviewGroupCounts((current) => ({ ...current, [group.id]: liveCount }));
        }
      }
    } catch (error) {
      if (request === groupPreviewRequest.current) setGroupPreviewError(getUserFacingErrorText(error, `We could not load the audience. Close this window and select View to try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`));
    } finally {
      if (request === groupPreviewRequest.current) setGroupPreviewLoading(false);
    }
  };
  const [audienceResult, setAudienceResult] = useState<{ message: string; tone: "success" | "error" } | null>(null);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [archiveGroup, setArchiveGroup] = useState<RecipientGroup | null>(null);
  const [archiving, setArchiving] = useState(false);
  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useGetAllCategoriesQuery({
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const {
    data: membershipData,
    loading: membershipLoading,
    error: membershipError,
    refetch: refetchMembership,
  } = useCmpMembershipSettings(
    isAudiencePopupOpen ? chamberId : "",
    "cache-and-network",
    EMAIL_OPERATION_ERROR_CONTEXT
  );
  const {
    data: governanceData,
    loading: governanceLoading,
    error: governanceError,
    refetch: refetchGovernance,
  } = useCmpGovernanceSettings(
    isAudiencePopupOpen || email.recipientSourceIds.includes("governanceGroups") ? chamberId : "",
    "cache-and-network",
    EMAIL_OPERATION_ERROR_CONTEXT
  );
  const {
    data: membersData,
    loading: membersLoading,
    error: membersError,
    refetch: refetchMembers,
  } = useGetCmpManageMembersQuery({
    variables: {
      input: {
        chamberId,
        includeRenewalEvidence: true,
        lifecycleStatuses: [
          CmpMemberLifecycleStatusEnum.Pending,
          CmpMemberLifecycleStatusEnum.Active,
          CmpMemberLifecycleStatusEnum.Suspended,
        ],
      },
    },
    skip: !chamberId || !isAudiencePopupOpen,
    fetchPolicy: "network-only",
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const [createCmpNewsletterRecipientGroup] =
    useCreateCmpNewsletterRecipientGroupMutation({
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const hasCurrentPreviewTotal = !isSentRecord && !isAudiencePreviewLoading && !audiencePreviewError &&
    !!recipientPreview && Number.isInteger(recipientPreview.uniqueValidRecipientCount) && recipientPreview.uniqueValidRecipientCount >= 0;
  const recipientSourceRows = useMemo(() => hasCurrentPreviewTotal && recipientPreview
    ? buildRecipientSourceRows(recipientPreview) : null, [hasCurrentPreviewTotal, recipientPreview]);
  const displayedRecipientCount = isSentRecord ? intendedRecipientCount
    : isAudiencePreviewLoading || audiencePreviewError ? null
      : hasCurrentPreviewTotal ? recipientPreview.uniqueValidRecipientCount : intendedRecipientCount;

  const industryOptions = useMemo(
    () =>
      [...(categoriesData?.getAllCategories || [])]
        .sort((firstCategory, secondCategory) =>
          firstCategory.name.localeCompare(secondCategory.name)
        )
        .map((category) => ({ id: String(category.id), label: category.name })),
    [categoriesData?.getAllCategories]
  );

  const popupMemberOptions: EmailAudienceMemberOption[] = useMemo(
    () =>
      (membersData?.getCmpManageMembers.rows || []).map((member) => ({
        id: member.id,
        companyName: member.companyName,
        lifecycleStatus: member.lifecycleStatus,
        membershipTypeId: member.membershipTypeId || "",
        membershipTypeName: member.membershipTypeName || "",
        dateJoinedUtcMilli: member.dateJoinedUtcMilli,
        fullTimeEmployees: member.fullTimeEmployees,
        partTimeEmployees: member.partTimeEmployees,
        renewalDateUtcMilli: member.renewalDateUtcMilli,
        lastRenewedAtUtcMilli: member.lastRenewedAtUtcMilli,
        categoryId: member.categoryId,
      })),
    [membersData?.getCmpManageMembers.rows]
  );

  const isEventAttendeesSelected =
    email.recipientSourceIds.includes("eventAttendees");
  const isEventSponsorsSelected =
    email.recipientSourceIds.includes("eventSponsors");
  const {
    data: manageEventsData,
    loading: manageEventsLoading,
    error: manageEventsError,
    refetch: refetchManageEvents,
  } = useGetCmpManageEventsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId || (!isEventAttendeesSelected && !isEventSponsorsSelected),
    fetchPolicy: "cache-and-network",
    context: EMAIL_OPERATION_ERROR_CONTEXT,
  });
  const eventAttendeeEventOptions = useMemo(
    () =>
      buildEventAttendeeEventOptions({
        events: manageEventsData?.getCmpManageEvents.events || [],
        selectedEventIds: email.eventIds,
      }),
    [email.eventIds, manageEventsData?.getCmpManageEvents.events]
  );
  const sponsorEventOptions = useMemo(
    () =>
      buildEventAttendeeEventOptions({
        events: manageEventsData?.getCmpManageEvents.events || [],
        selectedEventIds: email.sponsorEventIds,
      }),
    [email.sponsorEventIds, manageEventsData?.getCmpManageEvents.events]
  );
  const governanceGroupOptions = useMemo(
    () =>
      buildGovernanceGroupOptions({
        governance: governanceData?.getChamberMemberProSettings.governance,
        selectedGroupIds: email.governanceGroupIds,
      }),
    [email.governanceGroupIds, governanceData?.getChamberMemberProSettings.governance]
  );

  const customAudienceOptions = [
    ...recipientGroups.map((group) => ({ id: group.id, label: group.name })),
    ...email.recipientGroupIds.filter((id) => !recipientGroups.some((group) => group.id === id)).map((id) => ({ id, label: "Custom audience no longer available" })),
  ];

  const selectedFilterSummary = buildAudienceFilterSummary({
    audienceFilters,
    industryLabels: Object.fromEntries([
      ...industryOptions.map(({ id, label }) => [id, label]),
      ...memberTypeOptions.map(({ id, label }) => [`membershipType:${id}`, label]),
    ]),
  });
  const selectedListNames = customAudienceOptions
    .filter(({ id }) => email.recipientGroupIds.includes(id))
    .map(({ label }) => label);

  // F6 (Moeen, Oct 12 2026): Member Size and Member Since are no longer
  // offered. A saved email that still uses them shows them as removable chips,
  // so no hidden filter changes the audience.
  const legacyFilterChips = (["employeeRangeIds", "memberSinceRangeIds"] as const).flatMap((filterId) => {
    const labels = new Map(getAudienceRangeOptions({ filterId, values: audienceFilters[filterId] }).map(({ id, label }) => [id, label]));
    const prefix = filterId === "employeeRangeIds" ? "Member Size" : "Member Since";
    return audienceFilters[filterId].map((value) => ({ filterId, value, label: `${prefix}: ${labels.get(value) || value}` }));
  });

  const handleCustomAudienceChange = (nextGroupIds: string[]) => {
    updateEmailField("recipientGroupIds", nextGroupIds);
  };

  // Saves a new or edited custom audience and keeps the server-confirmed group
  // in the same query used by the route.
  const persistCustomAudience = async ({
    group,
    payload: { name, memberIds, eventIds, description, criteriaIds, directEmails, governanceGroupIds, sourceKinds, additionalRecipients },
  }: {
    group?: RecipientGroup;
    payload: CreateCustomAudiencePayload;
  }) => {
    if (!chamberId) {
      throw new UserFacingError(
        applyCmpOrganizationNoun(EMAIL_CHAMBER_LOADING_MESSAGE, noun)
      );
    }

    const data = { name, ...(description !== undefined ? { description } : {}), criteriaIds, memberIds, ...(eventIds !== undefined ? { eventIds } : {}), directEmails, governanceGroupIds, additionalRecipients, sourceKinds };
    const outcome = group
      ? (await client.mutate({
          mutation: UpdateCmpNewsletterRecipientGroupDocument,
          variables: { input: { chamberId, groupId: group.id, data } },
          context: EMAIL_OPERATION_ERROR_CONTEXT,
        })).data?.updateCmpNewsletterRecipientGroup
      : (await createCmpNewsletterRecipientGroup({
          variables: { input: { chamberId, data } },
        })).data?.createCmpNewsletterRecipientGroup;
    const savedGroup = outcome?.group;
    if (!outcome?.groupSavedSuccessfully || !savedGroup?.id) {
      throw new Error("The custom audience could not be saved.");
    }
    // A later list refresh must not make this saved change look like a failure.
    client.cache.updateQuery<GetCmpNewsletterRecipientGroupsQuery>(
      {
        query: GetCmpNewsletterRecipientGroupsDocument,
        variables: { input: { chamberId, includeArchived: false } },
      },
      (previous) => {
        const previousList = previous?.getCmpNewsletterRecipientGroups;
        return {
          ...previous,
          __typename: previous?.__typename ?? "Query",
          getCmpNewsletterRecipientGroups: {
            ...previousList,
            __typename: previousList?.__typename ?? "GetCmpNewsletterRecipientGroupsReturn",
            generatedAtUtcMilli:
              previousList?.generatedAtUtcMilli ??
              savedGroup.updatedAtUtcMilli ??
              savedGroup.createdAtUtcMilli ??
              Date.now(),
            groups: [
              ...(previousList?.groups || []).filter(
                (item) => item.id !== savedGroup.id
              ),
              savedGroup,
            ],
          },
        };
      }
    );
    return savedGroup;
  };

  /** Refetches the saved lists and returns them (empty when the read fails). */
  const refreshRecipientGroups = async (): Promise<RecipientGroup[]> => {
    try {
      const results = await client.refetchQueries({
        include: [GetCmpNewsletterRecipientGroupsDocument],
      });
      return (Array.isArray(results) ? results : []).flatMap((result) => {
        const data: GetCmpNewsletterRecipientGroupsQuery | undefined = result?.data;
        return (data?.getCmpNewsletterRecipientGroups?.groups || []).map(recipientGroupReturnToUiGroup);
      });
    } catch {
      // The canonical cache already contains the confirmed mutation result.
      return [];
    }
  };

  const createCustomAudience = async (payload: CreateCustomAudiencePayload) => {
    const savedGroup = await persistCustomAudience({ group: editingGroup, payload });
    clearLivePreviewGroupCount(savedGroup.id);
    // Save then select: the new audience becomes a checked option in the
    // SELECT CUSTOM AUDIENCE LISTS dropdown right away.
    if (!editingGroup && !email.recipientGroupIds.includes(savedGroup.id)) {
      toggleRecipientGroup(savedGroup.id);
    }
    setIsAudiencePopupOpen(false);
    setAudienceResult({
      message: editingGroup
        ? `Custom audience "${savedGroup.name}" has been updated successfully!`
        : "Your custom audience has been created successfully!",
      tone: "success",
    });
    await refreshRecipientGroups();
  };

  // Adds recipients to this email's audience (Estimated Audience view).
  const addRecipientsToEmail = (recipients: EmailAdditionalRecipient[]) => {
    const existing = new Map(
      email.additionalRecipients.map((recipient) => [
        recipient.email.toLowerCase(),
        recipient,
      ])
    );
    toNamedRecipientInputs(recipients).forEach((recipient) =>
      existing.set(recipient.email.toLowerCase(), { ...recipient, companyName: recipient.companyName || "" })
    );
    updateEmailField("additionalRecipients", Array.from(existing.values()));
    updateEmailField(
      "directRecipients",
      Array.from(
        new Set([
          ...email.directRecipients.split(/[\s,;]+/).filter(Boolean),
          ...recipients.map((recipient) => recipient.email),
        ])
      ).join(", ")
    );
    updateEmailField(
      "recipientSourceIds",
      Array.from(
        new Set<RecipientSourceId>([
          ...email.recipientSourceIds,
          "directRecipients",
        ])
      )
    );
  };

  // Adds recipients to a saved custom audience through the existing list save.
  const addRecipientsToGroup = async (group: RecipientGroup, recipients: EmailAdditionalRecipient[]) => {
    const savedGroup = await persistCustomAudience({
      group,
      payload: buildCustomAudiencePayload({
        name: group.name,
        initialGroup: group,
        selectedCategoryIds: getGroupCategoryIds(group),
        memberOptions: [],
        additionalRecipients: [
          ...(group.selection?.additionalRecipients || []),
          ...recipients,
        ],
      }),
    });
    void refreshRecipientGroups();
    // Reload the list view from the server so its rows and count include the
    // recipients just saved.
    void viewGroup(recipientGroupReturnToUiGroup(savedGroup));
  };

  const selectedAudienceBreakdown =
    recipientSourceRows && recipientSourceRows.length > 0
      ? [
          recipientSourceRows.map(({ label, count }) => `${label} (${formatNumber(count)})`).join(" \u2022 "),
          selectedFilterSummary ? `Filters: ${selectedFilterSummary}` : "",
        ].filter(Boolean).join("  \u2014  ")
      : "";

  return (
    <div className="space-y-6 [&>section>div:first-of-type:has(>span)]:!mb-5 [&>section>div:first-of-type>div:first-of-type:has(>span)]:!mb-5 [&>section>div:first-of-type_p]:!leading-[normal]">
      {isAudiencePopupOpen ? (
        <CreateCustomAudienceModal
          initialGroup={editingGroup}
          memberTypeOptions={(
            membershipData?.getChamberMemberProSettings?.membership
              ?.membershipModels || []
          ).filter(model => model.enabled).map((model) => ({ id: model.id, label: model.name }))}
          industryOptions={industryOptions}
          memberOptions={popupMemberOptions}
          governance={governanceData?.getChamberMemberProSettings.governance}
          isLoading={
            categoriesLoading ||
            membershipLoading ||
            governanceLoading ||
            membersLoading
          }
          loadError={
            categoriesError ||
            membershipError ||
            governanceError ||
            membersError
              ? emailLoadErrorText("the audience categories")
              : ""
          }
          onRetry={() => {
            void Promise.allSettled([
              refetchCategories(),
              refetchMembership(),
              refetchGovernance(),
              refetchMembers(),
            ]);
          }}
          onClose={() => setIsAudiencePopupOpen(false)}
          onCreate={createCustomAudience}
        />
      ) : null}

      {/* B10 (POL-002 Emails leg): shared SectionShell card with the
          transcription-bound numbered gold badge (variant="numbered"). */}
      <SectionShell
        id="email-step1-select-audience"
        variant="numbered"
        number={1}
        title="Select Your Audience"
        description="Choose from any of the predefined audience groups, or create your own custom audience group. You can view recipients below in Estimated Audience and add or delete recipients."
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {audienceCheckboxes.map((source) => (
            <div key={source.id} className="min-w-0">
              <label className="flex cursor-pointer items-center gap-2.5 text-[14px] font-normal leading-[normal] text-[#0A0A0A]">
                <Checkbox
                  sx={inlineChoiceControlSx}
                  checked={email.recipientSourceIds.includes(source.id)}
                  onChange={() => toggleRecipientSource(source.id)}
                  disabled={isBusy || isSentRecord}
                />
                {source.label}
              </label>
              <p className="ml-7 mt-1.5 text-[14px] leading-[1.45] text-[#727272]">{source.helper}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-3">
          {audiencePickerSources.map((source) => {
            const selected = email.recipientSourceIds.includes(source.id);
            const picker = source.id === "eventAttendees"
              ? { options: eventAttendeeEventOptions, values: email.eventIds, field: "eventIds" as const, placeholder: "Select one or more events", search: "Search Events", loading: manageEventsLoading && !manageEventsData, error: manageEventsError ? emailLoadErrorText("your events") : "", retry: () => void refetchManageEvents() }
              : source.id === "eventSponsors"
                ? { options: sponsorEventOptions, values: email.sponsorEventIds, field: "sponsorEventIds" as const, placeholder: "Select one or more events", search: "Search Events", loading: manageEventsLoading && !manageEventsData, error: manageEventsError ? emailLoadErrorText("your events") : "", retry: () => void refetchManageEvents() }
                : { options: governanceGroupOptions, values: email.governanceGroupIds, field: "governanceGroupIds" as const, placeholder: "Select one or more groups", search: "Search Groups", loading: governanceLoading && !governanceData, error: governanceError ? emailLoadErrorText("your groups") : "", retry: () => void refetchGovernance() };
            return (
              <div key={source.id} className="min-w-0">
                <label className="flex cursor-pointer items-center gap-2.5 text-[14px] font-normal leading-[normal] text-[#0A0A0A]">
                  <Checkbox
                    sx={inlineChoiceControlSx}
                    checked={selected}
                    onChange={() => toggleRecipientSource(source.id)}
                    disabled={isBusy || isSentRecord}
                  />
                  {source.label}
                </label>
                <p className="ml-7 mt-1.5 text-[14px] leading-[1.45] text-[#727272]">{source.helper}</p>
                {selected && (
                  <div className="mt-2.5">
                    <FilterDropdown
                      label={source.pickerLabel}
                      placeholder={picker.loading ? (source.id === "governanceGroups" ? "Loading groups..." : "Loading events...") : picker.placeholder}
                      searchPlaceholder={picker.search}
                      options={picker.options}
                      values={picker.values}
                      onChange={(values) => updateEmailField(picker.field, values)}
                      showChips
                      selectionDisplay="count"
                      disabled={isBusy || isSentRecord}
                      className="w-full [&>p]:text-[16px] [&>p]:font-semibold [&>p]:leading-[normal] [&>button]:!h-[42px]"
                    />
                    {picker.error ? (
                      <p role="alert" className="mt-2 text-sm text-red-600">
                        {picker.error}{" "}
                        <button type="button" className="font-semibold underline" onClick={picker.retry}>
                          Try again
                        </button>
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {email.recipientSourceIds.includes("nonMemberSubscribers") && (
          <p className="mt-3 text-sm text-[#727272]">
            Saved audience: Non Member Subscribers (Including website
            subscribers).
          </p>
        )}
        <div className="mt-6 flex flex-col gap-4 border-t border-[#ECECEC] pt-6">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-1">
              <h3 className="text-[16px] font-semibold leading-[normal] text-[#0A0A0A]">Custom Audience Lists</h3>
              <p className="text-[14px] leading-[1.45] text-[#0A0A0A]">
                Create custom contact lists, such as staff, community leaders, prospects or volunteers. Each list you create is saved here so you can reuse it in any future email. Check a list to include it in this email.
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-lg border-0 bg-cpaGold px-5 py-3 text-[14px] font-semibold leading-[normal] text-white hover:bg-cpaGoldDark disabled:opacity-50"
              onClick={() => { setEditingGroup(undefined); setIsAudiencePopupOpen(true); }}
              disabled={isBusy || isSentRecord}
            >
              + Create New Custom Audience List
            </button>
          </div>
          {customAudienceOptions.length > 0 ? (
            <>
              <div className="overflow-hidden overflow-x-auto rounded-[10px] border border-[#ECECEC]">
                <table className="w-full min-w-[612px] border-collapse text-left" aria-label="Custom audience lists">
                  <colgroup><col className="w-[52px]" /><col /><col className="w-[140px]" /><col className="w-[220px]" /></colgroup>
                  <thead>
                    <tr className="border-b border-[#ECECEC] bg-[#F7F7F8] text-[12px] font-semibold tracking-[.04em] text-[#727272]">
                      <th className="px-4 py-3"><span className="sr-only">Include</span></th>
                      <th className="px-[14px] py-3 font-semibold">SAVED CUSTOM LISTS</th>
                      <th className="px-[14px] py-3 font-semibold">CONTACTS</th>
                      <th className="px-[14px] py-3 font-semibold">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customAudienceOptions.map((option) => {
                      const group = recipientGroups.find((item) => item.id === option.id);
                      const groupCount = group ? livePreviewGroupCounts[group.id] ?? group.count : 0;
                      const checked = email.recipientGroupIds.includes(option.id);
                      return (
                        <tr key={option.id} className={`border-b border-[#F1F1F3] last:border-b-0 ${checked ? "bg-[#FDF8EE]" : "bg-white"}`}>
                          <td className="px-4 py-[14px] align-middle">
                            <Checkbox
                              sx={inlineChoiceControlSx}
                              checked={checked}
                              title="Include this list in this email"
                              inputProps={{ "aria-label": `Include ${option.label} in this email` }}
                              disabled={isBusy || isSentRecord}
                              onChange={() => toggleRecipientGroup(option.id)}
                            />
                          </td>
                          <td className="max-w-0 truncate px-[14px] py-[14px] text-[14px] font-semibold text-[#0A0A0A]" title={option.label}>{option.label}</td>
                          <td className="px-[14px] py-[14px] text-[14px] text-[#0A0A0A]">{group ? `${formatNumber(groupCount)} ${groupCount === 1 ? "contact" : "contacts"}` : "\u2014"}</td>
                          <td className="px-[14px] py-[10px]">
                            {group ? (
                              <div className="flex flex-wrap gap-2">
                                <button type="button" className="rounded-lg border-0 bg-[#23272F] px-3 py-[7px] text-[12px] font-semibold text-white" onClick={() => void viewGroup(group)}>View</button>
                                <button type="button" className="rounded-lg border-0 bg-cpaGold px-[14px] py-[7px] text-[12px] font-semibold text-white disabled:opacity-50" disabled={!group.selection || isBusy || isSentRecord} onClick={() => { setEditingGroup(group); setIsAudiencePopupOpen(true); }}>Edit</button>
                                <button type="button" className="rounded-lg border-0 bg-[#E24C4C] px-3 py-[7px] text-[12px] font-semibold text-white disabled:opacity-50" disabled={isBusy || isSentRecord} onClick={() => setArchiveGroup(group)}>Delete</button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {email.recipientGroupIds.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[14px] text-[#0A0A0A]">
                    <span className="font-bold">{email.recipientGroupIds.length} {email.recipientGroupIds.length === 1 ? "list" : "lists"}</span> included in this email
                  </p>
                  <button
                    type="button"
                    className="rounded-lg border border-[#F21B1B] bg-white px-[18px] py-2.5 text-[14px] font-semibold text-[#F21B1B] disabled:opacity-50"
                    disabled={isBusy || isSentRecord}
                    onClick={() => handleCustomAudienceChange([])}
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-[10px] border border-dashed border-[#C9C9C9] p-6 text-center text-[14px] text-[#727272]">
              You haven{"\u2019"}t created any custom audience lists yet. Lists you create will be saved here for future emails.
            </div>
          )}
        </div>
      </SectionShell>

      <SectionShell
        id="email-step1-refine-audience"
        variant="numbered"
        number={2}
        title={
          <>
            Refine Your Audience with Filters{" "}
            <span className="text-[#9E9E9E]">(Optional)</span>
          </>
        }
        description="Use filters to refine your selected audience to make your email message more specific."
        headerActions={
          <button
            type="button"
            className={`${dangerButtonClassName} !border-0 !px-5 !py-3 !leading-[normal]`}
            onClick={clearAllFilters}
            disabled={isBusy || isSentRecord}
          >
            Clear All Filters
          </button>
        }
      >
        <div className={isSentRecord ? "pointer-events-none opacity-60" : ""}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 [&>div>p]:leading-[normal] [&>div>button]:!h-[42px]">
            <FilterDropdown
              label="Member Type"
              searchPlaceholder="Search"
              disabled={isBusy || isSentRecord}
              placeholder="Filter by membership type"
              options={memberTypeOptions}
              values={audienceFilters.membershipTypeIds}
              onChange={(values) =>
                updateAudienceFilter("membershipTypeIds", values)
              }
            />
            <FilterDropdown
              label="Category"
              searchPlaceholder="Search"
              disabled={isBusy || isSentRecord}
              placeholder="Filter by category"
              options={industryOptions}
              values={audienceFilters.categoryIds}
              onChange={(values) => updateAudienceFilter("categoryIds", values)}
            />
            <FilterDropdown
              label="Renewal Status"
              searchPlaceholder="Search"
              disabled={isBusy || isSentRecord}
              placeholder="Filter by up and coming renewal"
              options={getAudienceRangeOptions({
                filterId: "renewalRangeIds",
                values: audienceFilters.renewalRangeIds,
              })}
              values={audienceFilters.renewalRangeIds}
              onChange={(values) =>
                updateAudienceFilter("renewalRangeIds", values)
              }
            />
          </div>
        </div>
        {legacyFilterChips.length > 0 && (
          <div className="mt-5">
            <p className="text-[13px] text-[#727272]">
              This saved email also uses a filter that is no longer offered. It still applies until you remove it.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {legacyFilterChips.map((chip) => (
                <span key={`${chip.filterId}:${chip.value}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#ECECEC] bg-[#F7F7F8] py-1 pl-3 pr-1.5 text-[13px] text-[#0A0A0A]">
                  {chip.label}
                  <button
                    type="button"
                    aria-label={`Remove filter ${chip.label}`}
                    disabled={isBusy || isSentRecord}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[#727272] hover:bg-[#ECECEC]"
                    onClick={() => updateAudienceFilter(chip.filterId, audienceFilters[chip.filterId].filter((value) => value !== chip.value))}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </SectionShell>

      <SectionShell
        id="email-step1-estimated-audience"
        variant="numbered"
        number={3}
        title="Estimated Audience"
        description={<>Review your audience size before entering your Campaign Details. You can delete and add any new recipients to this campaign.<br /><strong>NOTE:</strong> When recipients are added or deleted, they are done only for this campaign.</>}
      >
        <div className="flex flex-wrap items-center gap-7">
        <div className="inline-flex min-h-[100px] w-full max-w-[218px] flex-col items-center justify-center rounded-lg border border-[#ECECEC] bg-white px-[34px] py-[22px] text-center">
          <p className="text-[32px] font-bold leading-none text-black">
            {displayedRecipientCount === null
              ? "—"
              : formatNumber(displayedRecipientCount)}
          </p>
          <p
            className={`mt-1.5 flex items-center justify-center gap-2 text-[12px] font-bold uppercase leading-[normal] tracking-[.05em] ${
              audiencePreviewError
                ? "text-red-600"
                : isAudiencePreviewLoading
                  ? "text-gray-500"
                  : "text-[#10B981]"
            }`}
            aria-live="polite"
          >
            {isAudiencePreviewLoading ? (
              <CircularProgress color="inherit" size={14} />
            ) : null}
            {audiencePreviewError
              ? "Estimate unavailable"
              : isAudiencePreviewLoading
                ? "Calculating recipients"
                : "Estimated Recipients"}
          </p>
          {audiencePreviewError ? (
            <p className="mt-2 max-w-sm text-[13px] leading-5 text-red-600">
              {audiencePreviewError}
            </p>
          ) : null}
        </div>
        <div className="max-w-[520px] text-[13px] leading-[1.6] text-[#0A0A0A]">
          <p aria-label="Recipients by source">
            {recipientSourceRows === null
              ? isSentRecord ? "Recipient source counts are not available for this saved email."
                : audiencePreviewError ? "We could not count your recipients by source. Refresh the page to try again."
                  : isAudiencePreviewLoading ? "We are still counting your recipients."
                    : recipientPreview ? "We could not show the recipient count for every source."
                      : "Choose an audience to see the recipient breakdown."
              : recipientSourceRows.length === 0 ? "No one in this audience can receive this email. Choose another audience."
                : recipientSourceRows.map(({ label, count }) => `${label} (${formatNumber(count)})`).join(" • ")}
          </p>
          {selectedListNames.length > 0 && (
            <p>Saved lists: {selectedListNames.join(", ")}</p>
          )}
          {selectedFilterSummary && <p>Filters: {selectedFilterSummary}</p>}
        </div>
        </div>
        <button
          type="button"
          className={`${primaryButtonClassName} mt-4 !h-[40px] !min-h-[40px] w-[218px] max-w-full`}
          disabled={isAudiencePreviewLoading || !recipientPreview}
          onClick={() => setRecipientsOpen(true)}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
          View Recipients List
        </button>
      </SectionShell>
      {viewingGroup && (
        <RecipientsListModal
          variant="customList"
          title={viewingGroup.name}
          description="Members currently in this custom audience list."
          preview={groupPreview} previewLoading={groupPreviewLoading} previewError={groupPreviewError}
          email={email}
          disabled={isBusy || isSentRecord}
          addDisabled={!viewingGroup.selection}
          knownEmails={[
            ...(viewingGroup.selection?.directEmails || []),
            ...(viewingGroup.selection?.additionalRecipients || []).map((recipient) => recipient.email),
          ]}
          onClose={() => { ++groupPreviewRequest.current; setViewingGroup(null); }}
          updateEmailField={updateEmailField}
          onAddRecipients={(recipients) => addRecipientsToGroup(viewingGroup, recipients)}
          onRemoveFromList={async (recipientKey) => {
            const groupId = viewingGroup.id;
            await removeRecipientFromCustomAudience({ chamberId, client, criterionId: groupId, recipientKey });
            // Reload the pop-up rows and count, and the saved lists table. The
            // refreshed list then replaces the copy taken when View was
            // selected, so a later add in this pop-up builds on the saved list.
            void viewGroup(viewingGroup);
            void refreshRecipientGroups().then((groups) => {
              const refreshed = groups.find((group) => group.id === groupId);
              if (refreshed) setViewingGroup((current) => (current?.id === groupId ? refreshed : current));
            });
          }}
        />
      )}
      {recipientsOpen && (
        <RecipientsListModal
          variant="estimated"
          // Design (20) recipientsModalTitle (CMP Dashboard.dc.html:18778),
          // with clean wording instead of the design text (review section F).
          title={ESTIMATED_AUDIENCE_MODAL_TITLE}
          description={selectedAudienceBreakdown}
          preview={recipientPreview}
          previewLoading={isAudiencePreviewLoading}
          previewError={audiencePreviewError}
          email={email}
          disabled={isBusy || isSentRecord}
          addDisabled={isBusy || isSentRecord}
          knownEmails={[
            ...email.additionalRecipients.map((recipient) => recipient.email),
            ...email.directRecipients.split(/[\s,;]+/),
          ]}
          onClose={() => setRecipientsOpen(false)}
          updateEmailField={updateEmailField}
          onAddRecipients={addRecipientsToEmail}
        />
      )}
      <ActionSuccessDialog
        message={audienceResult?.message}
        tone={audienceResult?.tone}
        onClose={() => setAudienceResult(null)}
      />
      <ConfirmDialog
        open={!!archiveGroup}
        variant="destructive"
        title="Delete Custom Audience"
        message={`Delete \u201C${archiveGroup?.name || ""}\u201D? It will be removed from any email currently using it. This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={archiving}
        onClose={() => {
          if (!archiving) setArchiveGroup(null);
        }}
        onConfirm={async () => {
          if (!archiveGroup) return;
          setArchiving(true);
          try {
            // The backend archives the list, so saved email records keep
            // their audience history. EmailForm reports the result.
            await archiveRecipientGroup(archiveGroup);
            setArchiveGroup(null);
          } catch (error) {
            setArchiveGroup(null);
            setAudienceResult({
              message: getUserFacingErrorText(
                error,
                buildStaffErrorFallback(
                  `delete the custom audience "${archiveGroup.name}"`
                )
              ),
              tone: "error",
            });
          } finally {
            setArchiving(false);
          }
        }}
      />
    </div>
  );
};

export default StepOneAudience;
